import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, TableShell, Field } from "@/components/ui-kit";
import { AlertTriangle, Gauge, CheckCircle2 } from "lucide-react";

const TYPE_OPTS = [
  { value: "", label: "All types" },
  { value: "off_route", label: "Off route" },
  { value: "speeding", label: "Speeding" },
];

function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AlertHistory() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState({ type: "", driver_id: "", from_date: "", to_date: "" });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { include_acknowledged: true };
    if (filter.type) params.type = filter.type;
    if (filter.driver_id) params.driver_id = filter.driver_id;
    if (filter.from_date) params.from_date = filter.from_date;
    if (filter.to_date) params.to_date = filter.to_date;
    api.get("/alerts", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data));
    load();
    // eslint-disable-next-line
  }, [filter]);

  const driverOpts = useMemo(
    () => [{ value: "", label: "All drivers" }, ...drivers.map((d) => ({ value: d.id, label: d.name }))],
    [drivers]
  );
  const driverName = (id) => drivers.find((d) => d.id === id)?.name || "—";

  const totals = useMemo(() => {
    return {
      total: rows.length,
      off_route: rows.filter((r) => r.type === "off_route").length,
      speeding: rows.filter((r) => r.type === "speeding").length,
      unack: rows.filter((r) => !r.acknowledged_at).length,
    };
  }, [rows]);

  return (
    <div data-testid="alert-history-page">
      <PageHeader subtitle="Safety" title="Alert History" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          ["Total", totals.total],
          ["Unacknowledged", totals.unack],
          ["Off-route", totals.off_route],
          ["Speeding", totals.speeding],
        ].map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
            <div className="font-display text-xl font-bold text-[#0A192F] mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Field label="Type" testId="filter-type" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} options={TYPE_OPTS} />
        <Field label="Driver" testId="filter-driver" value={filter.driver_id} onChange={(e) => setFilter({ ...filter, driver_id: e.target.value })} options={driverOpts} />
        <Field label="From" testId="filter-from" type="date" value={filter.from_date} onChange={(e) => setFilter({ ...filter, from_date: e.target.value })} />
        <Field label="To" testId="filter-to" type="date" value={filter.to_date} onChange={(e) => setFilter({ ...filter, to_date: e.target.value })} />
      </div>

      <TableShell
        columns={["When", "Type", "Patient", "Driver", "Detail", "Status"]}
        empty={!loading && rows.length === 0 ? "No alerts match these filters." : null}
      >
        {rows.map((a) => (
          <tr key={a.id} className="tbl-row border-t border-slate-100" data-testid={`history-row-${a.id}`}>
            <td className="px-4 py-2.5 text-xs font-mono">{fmtWhen(a.at)}</td>
            <td className="px-4 py-2.5">
              {a.type === "speeding" ? (
                <span className="inline-flex items-center gap-1 text-xs bg-rose-50 text-rose-800 border border-rose-200 rounded-full px-2 py-0.5"><Gauge size={11} /> Speeding</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5"><AlertTriangle size={11} /> Off route</span>
              )}
            </td>
            <td className="px-4 py-2.5 font-medium">{a.patient_name}</td>
            <td className="px-4 py-2.5">{driverName(a.driver_id)}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-rose-700">
              {a.type === "speeding" ? `${a.speed_mph} / ${a.limit_mph} mph · ${a.plate || ""}` : `${a.meters} m off route`}
            </td>
            <td className="px-4 py-2.5 text-xs">
              {a.acknowledged_at ? (
                <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Acknowledged</span>
              ) : (
                <span className="text-rose-700">Open</span>
              )}
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
}
